// Thin wrappers sobre apiFetch para los 9 endpoints de /v1/auth/mfa/* + el
// admin reset en /v1/team/:userId/reset-mfa. Cero state — los hooks deciden
// cuándo invalidar query keys.

import { apiFetch } from '@/lib/query-client';

import type {
  AdminResetMfaInput,
  AdminResetMfaResponse,
  BackupCodeVerifyResponse,
  ChallengeResponse,
  EnrollTotpResponse,
  MfaStatus,
  RegenerateBackupCodesResponse,
  RevokeAllDevicesResponse,
  TrustedDevicesListResponse,
  VerifyEnrollmentResponse,
  VerifySuccessResponse,
} from '../types';

export async function getMfaStatus(): Promise<MfaStatus> {
  return apiFetch<MfaStatus>({ path: '/v1/auth/mfa/status' });
}

export async function enrollTotp(): Promise<EnrollTotpResponse> {
  return apiFetch<EnrollTotpResponse>({
    path: '/v1/auth/mfa/enroll-totp',
    method: 'POST',
  });
}

export async function verifyEnrollment(input: {
  readonly factor_id: string;
  readonly code: string;
}): Promise<VerifyEnrollmentResponse> {
  return apiFetch<VerifyEnrollmentResponse>({
    path: '/v1/auth/mfa/verify-enrollment',
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function createChallenge(input: {
  readonly factor_id: string;
}): Promise<ChallengeResponse> {
  return apiFetch<ChallengeResponse>({
    path: '/v1/auth/mfa/challenge',
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function verifyCode(input: {
  readonly factor_id: string;
  readonly challenge_id: string;
  readonly code: string;
  readonly trust_device?: boolean;
}): Promise<VerifySuccessResponse> {
  return apiFetch<VerifySuccessResponse>({
    path: '/v1/auth/mfa/verify',
    method: 'POST',
    body: JSON.stringify(input),
    // Cookie __Host-trust-device se setea por backend en Set-Cookie. fetch
    // por default acepta cookies same-site del response.
    credentials: 'include',
  });
}

export async function verifyBackupCode(input: {
  readonly plain_code: string;
}): Promise<BackupCodeVerifyResponse> {
  return apiFetch<BackupCodeVerifyResponse>({
    path: '/v1/auth/mfa/backup-code',
    method: 'POST',
    body: JSON.stringify(input),
    credentials: 'include',
  });
}

export async function regenerateBackupCodes(): Promise<RegenerateBackupCodesResponse> {
  return apiFetch<RegenerateBackupCodesResponse>({
    path: '/v1/auth/mfa/regenerate-backup-codes',
    method: 'POST',
  });
}

export async function listTrustedDevices(): Promise<TrustedDevicesListResponse> {
  return apiFetch<TrustedDevicesListResponse>({
    path: '/v1/auth/mfa/trusted-devices',
  });
}

export async function revokeTrustedDevice(deviceId: string): Promise<void> {
  await apiFetch<void>({
    path: `/v1/auth/mfa/trusted-devices/${deviceId}`,
    method: 'DELETE',
  });
}

export async function revokeAllTrustedDevices(): Promise<RevokeAllDevicesResponse> {
  return apiFetch<RevokeAllDevicesResponse>({
    path: '/v1/auth/mfa/trusted-devices',
    method: 'DELETE',
  });
}

export async function adminResetMfa(
  userId: string,
  input: AdminResetMfaInput,
): Promise<AdminResetMfaResponse> {
  return apiFetch<AdminResetMfaResponse>({
    path: `/v1/team/${userId}/reset-mfa`,
    method: 'POST',
    body: JSON.stringify(input),
  });
}
